<!-- add a time limit to the email verification link -->
 <!-- should be able to resend the email verification link -->
  <!-- should be able to see that the email is verified, then will click continue and will
   land to the student dashboard -->
  <!-- should be able to go back to the previous step -->
  <!-- should be able to go to the next step -->
  <!-- should be able to submit the form -->
  <!-- should be able to show the error messages -->
  <!-- should be able to show the success messages -->
  <!-- should be able to show the loading state -->
<template>
  <div class="email-verification-container">
    <div class="verification-wrapper">
      <!-- Main Verification Card -->
      <div class="verification-card">
        <!-- Top Icon -->
        <div class="envelope-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>
          <div class="notification-circle">
            <span>1</span>
          </div>
        </div>
        
        <!-- Main Title -->
        <h1 class="main-title">Check Your Email</h1>
        
        <!-- Instructional Text -->
        <p class="instruction-text">
          We've sent a verification email to <strong>{{ email }}</strong>. 
          Please check your inbox and click the verification link to complete your registration.
        </p>
        
        <!-- Spam Notice -->
        <div class="spam-notice">
          <svg viewBox="0 0 24 24" fill="currentColor" class="info-icon">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <div class="spam-text">
            <strong>Can't find the email?</strong>
            <p>Check your <strong>spam</strong> or <strong>junk</strong> folder. Sometimes our emails end up there.</p>
          </div>
        </div>
        
        <!-- Status Message -->
        <div v-if="emailVerified" class="status-message success">
          <p>🎉 Email verified successfully!</p>
          <p class="note">Your email has been verified. You will be redirected to your dashboard shortly.</p>
        </div>
        
        <div v-if="verificationError" class="status-message error">
          <p>❌ {{ verificationError }}</p>
          <p class="note">Please try clicking the verification link again or request a new verification email.</p>
        </div>
        
        <div v-if="errorMessage" class="status-message error">
          <p>❌ {{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

// Props
interface Props {
  modelValue: {
    verificationCode: string;
    email: string;
  };
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: { verificationCode: string; email: string }];
  'verification-complete': [value: { verificationCode: string; email: string }];
}>();

// Local state
const email = computed(() => props.modelValue.email);
const emailVerified = ref(false);
const verificationError = ref('');
const errorMessage = ref('');

// Watch for verification code changes from parent
watch(() => props.modelValue.verificationCode, (newCode) => {
  if (newCode === 'verified') {
    emailVerified.value = true;
    verificationError.value = '';
  } else if (newCode === 'error') {
    verificationError.value = 'Email verification failed. Please try again.';
    emailVerified.value = false;
  }
});
</script>

<style scoped>
.email-verification-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
}

.verification-wrapper {
  width: 100%;
  max-width: 500px;
}

/* Verification Card */
.verification-card {
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
}

/* Envelope Icon */
.envelope-icon {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 32px auto;
  color: #fbbf24;
}

.envelope-icon svg {
  width: 100%;
  height: 100%;
}

.notification-circle {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 32px;
  height: 32px;
  background: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 16px;
  border: 3px solid white;
}

/* Main Title */
.main-title {
  color: #1f2937;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 20px 0;
  line-height: 1.2;
}

/* Instructional Text */
.instruction-text {
  color: #4b5563;
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 40px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.instruction-text strong {
  color: #1f2937;
  font-weight: 600;
}

/* Spam Notice */
.spam-notice {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 16px;
  margin: 0 auto 32px auto;
  max-width: 400px;
  display: flex;
  gap: 12px;
  text-align: left;
}

.spam-notice .info-icon {
  width: 24px;
  height: 24px;
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
}

.spam-text {
  flex: 1;
}

.spam-text strong {
  color: #92400e;
  font-size: 14px;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.spam-text p {
  color: #78350f;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.spam-text p strong {
  display: inline;
  font-weight: 600;
  color: #92400e;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 300px;
  margin: 0 auto;
}

/* Status Message */
.status-message {
  margin-top: 30px;
  padding: 15px;
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.5;
}

.status-message.success {
  background-color: #e0f2fe;
  border: 1px solid #a7d7ff;
  color: #075985;
}

.status-message.error {
  background-color: #fef3f2;
  border: 1px solid #fca5a5;
  color: #991b1b;
}

.status-message .note {
  font-size: 13px;
  color: #4b5563;
  margin-top: 5px;
}


/* Responsive Design */
@media (max-width: 768px) {
  .verification-card {
    padding: 40px 24px;
  }
  
  .main-title {
    font-size: 24px;
  }
  
  .instruction-text {
    font-size: 15px;
  }
  
  .envelope-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 24px;
  }
  
  .notification-circle {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .email-verification-container {
    padding: 16px;
  }
  
  .verification-card {
    padding: 32px 20px;
    border-radius: 12px;
  }
  
  .main-title {
    font-size: 22px;
  }
  
  .instruction-text {
    font-size: 14px;
  }
  
  .action-buttons {
    max-width: 100%;
  }
}
</style>
